// Thanh trên (N2, QĐ-H50, H59, H65, H66): KHSX · 6 menu (+ số đỏ việc trễ) · độ tươi · chọn nhãn (áp toàn web) · người dùng + vai trò.
import { useRef, useState } from "react";
import { type AppUser, laAdmin } from "../api/appUser";
import { dangXuat } from "../api/auth";
import type { DongNhan } from "../api/voApp";
import { type ChonNhan, chuDoTuoi, chuVietTat, gioCapNhatMoiNhat, type KhoaMenu, MENU, type NguonDoTuoi, TAT_CA } from "../core/voApp";
import { Popover } from "../ui/Popover";

export interface ThanhTrenProps {
  appUser: AppUser;
  manHienTai: KhoaMenu;
  onChonMan: (m: KhoaMenu) => void;
  nhan: ChonNhan;
  dsNhan: readonly DongNhan[];
  onChonNhan: (n: ChonNhan) => void;
  doTuoi: readonly NguonDoTuoi[];
  /** Số đỏ trên menu (Tác vụ = việc trễ hạn, Nghỉ & Dạy thay = ca Tiếp nhận đã qua ngày — QĐ-H78). */
  soDo?: Partial<Record<KhoaMenu, number>>;
  /** "cu" = làm mới lỗi, đang dùng dữ liệu đã lưu · "loi" = chưa có dữ liệu nào · null = bình thường */
  trangThaiNen?: "cu" | "loi" | null;
  onThuLaiNen?: () => void;
}

/** Màu chấm của nhãn: IM / CU theo token; nhãn khác dùng màu đã lưu hoặc xám. */
function mauNhan(n: Pick<DongNhan, "ma" | "mau">): string {
  if (n.ma === "IM") return "var(--im)";
  if (n.ma === "CU") return "var(--cu)";
  return n.mau && /^#[0-9a-f]{3,8}$/i.test(n.mau) ? n.mau : "var(--line-strong)";
}

export function ThanhTren({ appUser, manHienTai, onChonMan, nhan, dsNhan, onChonNhan, doTuoi, soDo, trangThaiNen, onThuLaiNen }: ThanhTrenProps) {
  const nhanRef = useRef<HTMLButtonElement>(null);
  const userRef = useRef<HTMLButtonElement>(null);
  const [moNhan, setMoNhan] = useState(false);
  const [moUser, setMoUser] = useState(false);
  const chuTuoi = chuDoTuoi(doTuoi, Date.now());
  const gioMoi = gioCapNhatMoiNhat(doTuoi, Date.now());
  const nhanDangChon = dsNhan.find((n) => n.ma === nhan);
  const admin = laAdmin(appUser);

  return (
    <header className="top">
      <div className="brand"><span className="brand-mark" aria-hidden>KH</span>KHSX</div>
      <nav className="nav" aria-label="Menu chính">
        {MENU.map((m) => (
          <button key={m.khoa} type="button" aria-current={m.khoa === manHienTai ? "page" : undefined} onClick={() => onChonMan(m.khoa)}>
            {m.ten}
            {!!soDo?.[m.khoa] && <span className="count" aria-label={`${soDo[m.khoa]} việc trễ`}>{soDo[m.khoa]}</span>}
          </button>
        ))}
      </nav>
      <div className="top-right">
        {trangThaiNen && (
          <button type="button" className={"miss nen-" + trangThaiNen} onClick={onThuLaiNen} title="Bấm để thử tải lại">
            {trangThaiNen === "cu" ? "⚠ Mất kết nối — đang dùng dữ liệu đã lưu" : "⚠ Không tải được dữ liệu — thử lại"}
          </button>
        )}
        {gioMoi && <span className="fresh" title={"Dữ liệu cập nhật: " + chuTuoi}>Cập nhật <b>{gioMoi}</b></span>}
        <button ref={nhanRef} type="button" className="nhanbtn" aria-haspopup="menu" aria-expanded={moNhan} onClick={() => setMoNhan((x) => !x)}
          title="Nhãn đang xem — áp cho toàn web (chỉ là bộ lọc, không chặn quyền)">
          {nhanDangChon ? <span className="dot" style={{ background: mauNhan(nhanDangChon) }} /> : null}
          {nhanDangChon ? nhanDangChon.ma : "Tất cả nhãn"}
          <span className="caret" aria-hidden>▾</span>
        </button>
        <Popover mo={moNhan} moc={nhanRef} onDong={() => setMoNhan(false)} canPhai nhan="Chọn nhãn">
          <div className="optlist" role="menu">
            <button type="button" role="menuitemradio" className="opt" aria-checked={nhan === TAT_CA} onClick={() => { onChonNhan(TAT_CA); setMoNhan(false); }}>
              <span className="dot" style={{ background: "var(--ink-3)" }} />Tất cả nhãn
            </button>
            {dsNhan.map((n) => (
              <button key={n.ma} type="button" role="menuitemradio" className="opt" aria-checked={nhan === n.ma} onClick={() => { onChonNhan(n.ma); setMoNhan(false); }}>
                <span className="dot" style={{ background: mauNhan(n) }} />{n.ma}
                {n.ten && n.ten !== n.ma && <small>{n.ten}</small>}
                {appUser.nhan_mac_dinh === n.ma && <small>mặc định</small>}
              </button>
            ))}
          </div>
        </Popover>
        <button ref={userRef} type="button" className="user" aria-haspopup="menu" aria-expanded={moUser} onClick={() => setMoUser((x) => !x)}>
          <span className="avatar" aria-hidden>{chuVietTat(appUser.ho_ten, appUser.email)}</span>
          <span className="user-ten">{appUser.ho_ten ?? appUser.email}</span>
          {admin && <span className="role">Admin</span>}
        </button>
        <Popover mo={moUser} moc={userRef} onDong={() => setMoUser(false)} canPhai nhan="Tài khoản">
          <div className="optlist" role="menu">
            <div style={{ padding: "4px 10px 8px", borderBottom: "1px solid var(--line)", marginBottom: 4 }}>
              <b>{appUser.ho_ten ?? "—"}</b>
              <div className="small muted">{appUser.email}</div>
              <div className="small muted">Vai trò: {admin ? "Admin" : "Nhân sự"}</div>
            </div>
            <button type="button" role="menuitem" className="opt" onClick={() => void dangXuat()}>Đăng xuất</button>
          </div>
        </Popover>
      </div>
    </header>
  );
}
